import React, { useState } from 'react';
import { compressPdf } from '../utils/compressPdf';

const CompressPDF = () => {
    const [file, setFile] = useState(null);
    const [compressedFile, setCompressedFile] = useState(null);
    const [converting, setConverting] = useState(false);
    const [error, setError] = useState('');
    const [originalSize, setOriginalSize] = useState(0);
    const [compressedSize, setCompressedSize] = useState(0);
    const fileInputRef = React.useRef(null);

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'application/pdf') {
                setError('Please upload a valid PDF file.');
                setFile(null);
                setOriginalSize(0);
                return;
            }
            setFile(selectedFile);
            setOriginalSize(selectedFile.size);
            setError('');
            setCompressedFile(null);
            setCompressedSize(0);
        }
    };

    const handleCompress = async (e) => {
        // Blur the button to remove focus state
        if (e && e.target) {
            e.target.blur();
        }

        if (!file) {
            setError('Please select a PDF file first.');
            return;
        }

        setConverting(true);
        setError('');

        try {
            const compressedBlob = await compressPdf(file);
            setCompressedFile(compressedBlob);
            setCompressedSize(compressedBlob.size);

            // Auto download
            const url = URL.createObjectURL(compressedBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `compressed_${file.name}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            setError('Error compressing file. Please try again.');
        } finally {
            setConverting(false);

            // Auto reset after 5 seconds - Or immediate?
            // User requested "come to original color", implying immediate interaction reset is preferred.
            // Let's reset file input immediately after download, similar to others.

            setFile(null);
            setCompressedFile(null);
            setOriginalSize(0);
            setCompressedSize(0);
            setError('');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <div className="converter-section">
            <h2>Compress PDF</h2>

            <div className="input-group">
                <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="file-input"
                    ref={fileInputRef}
                />
            </div>

            {file && (
                <div style={{ marginBottom: '1rem', color: '#475569' }}>
                    <p>Original Size: <strong>{formatBytes(originalSize)}</strong></p>
                </div>
            )}

            {compressedFile && (
                <div style={{ marginBottom: '1rem', color: '#10b981' }}>
                    <p>Compressed Size: <strong>{formatBytes(compressedSize)}</strong></p>
                    <p>Reduction: <strong>{((originalSize - compressedSize) / originalSize * 100).toFixed(1)}%</strong></p>
                </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <button
                onClick={handleCompress}
                className="convert-btn"
                disabled={converting || !file}
            >
                {converting ? 'Compressing...' : 'Compress PDF'}
            </button>

            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
                Note: Compression ratio depends on the file content.
            </p>
        </div>
    );
};

export default CompressPDF;
